import { Controller, SearchEngine } from '@coveo/atomic-react';
import { useController } from '@lib/hooks/useController';
import { useCallback, useMemo } from 'react';
import { useEngine } from '../EngineContext';
import { useOptimisticFacetSelection } from './useOptimisticFacetSelection';
import { useStableFacetValues } from './useStableFacetValues';

export type StringFlatFacetValue = {
  value: string;
  state: string;
  numberOfResults: number;
};

export type NumericFlatFacetValue = {
  start: number;
  end: number;
  endInclusive: boolean;
  state: string;
  numberOfResults: number;
};

export type DateFlatFacetValue = {
  start: string;
  end: string;
  endInclusive: boolean;
  state: string;
  numberOfResults: number;
};

type FlatFacetValue =
  StringFlatFacetValue | NumericFlatFacetValue | DateFlatFacetValue;

type FlatFacetController<TValue extends FlatFacetValue> = Controller & {
  state: { values: TValue[] };
  deselectAll(): void;
  toggleSelect(value: TValue): void;
};

export function useFlatFacet<TValue extends FlatFacetValue>({
  getController,
  fieldName,
  getValueId,
  seedValues,
}: {
  getController: (engine: SearchEngine) => FlatFacetController<TValue>;
  fieldName: string;
  getValueId: (value: TValue) => string;
  seedValues?: TValue[];
}) {
  const eng = useEngine();
  const controller = useMemo(() => getController(eng), [eng, getController]);
  const state = useController(controller);
  const stableValues = useStableFacetValues(
    state.values,
    fieldName,
    eng,
    seedValues
  );
  const { optimisticValues, selectedCount, findStableValue, applyOptimistic } =
    useOptimisticFacetSelection(stableValues);

  const handleClear = useCallback(() => {
    controller.deselectAll();
  }, [controller]);

  const selectedIds = useMemo(
    () =>
      optimisticValues.filter((v) => v.state === 'selected').map(getValueId),
    [optimisticValues, getValueId]
  );

  const handleSelectMany = useCallback(
    ([id]: string[], _selected: boolean) => {
      const optimistic = optimisticValues.find((v) => getValueId(v) === id);
      if (!optimistic) return;
      const stableValue = findStableValue(optimistic);
      if (!stableValue) return;
      applyOptimistic([stableValue], () =>
        controller.toggleSelect(stableValue)
      );
      controller.toggleSelect(stableValue);
    },
    [optimisticValues, findStableValue, applyOptimistic, controller, getValueId]
  );

  return {
    stableValues,
    optimisticValues,
    selectedIds,
    selectedCount,
    handleClear,
    handleSelectMany,
  };
}
